import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { HelpCircle, MessageCircle, Mail, Phone, Book } from 'lucide-react';

const HelpPage = () => {
  const { t } = useTranslation();

  const faqs = [
    {
      category: t('helpPage.faq1Cat'),
      questions: [
        { q: t('helpPage.faq1q1'), a: t('helpPage.faq1a1') },
        { q: t('helpPage.faq1q2'), a: t('helpPage.faq1a2') },
        { q: t('helpPage.faq1q3'), a: t('helpPage.faq1a3') },
      ],
    },
    {
      category: t('helpPage.faq2Cat'),
      questions: [
        { q: t('helpPage.faq2q1'), a: t('helpPage.faq2a1') },
        { q: t('helpPage.faq2q2'), a: t('helpPage.faq2a2') },
        { q: t('helpPage.faq2q3'), a: t('helpPage.faq2a3') },
      ],
    },
    {
      category: t('helpPage.faq3Cat'),
      questions: [
        { q: t('helpPage.faq3q1'), a: t('helpPage.faq3a1') },
        { q: t('helpPage.faq3q2'), a: t('helpPage.faq3a2') },
        { q: t('helpPage.faq3q3'), a: t('helpPage.faq3a3') },
      ],
    },
  ];

  const contactMethods = [
    {
      icon: MessageCircle,
      title: t('helpPage.contact1Title'),
      description: t('helpPage.contact1Desc'),
      action: t('helpPage.contact1Action'),
      available: t('helpPage.contact1Available'),
    },
    {
      icon: Mail,
      title: t('helpPage.contact2Title'),
      description: t('helpPage.contact2Desc'),
      action: t('helpPage.contact2Action'),
      available: t('helpPage.contact2Available'),
    },
    {
      icon: Phone,
      title: t('helpPage.contact3Title'),
      description: t('helpPage.contact3Desc'),
      action: t('helpPage.contact3Action'),
      available: t('helpPage.contact3Available'),
    },
    {
      icon: Book,
      title: t('helpPage.contact4Title'),
      description: t('helpPage.contact4Desc'),
      action: t('helpPage.contact4Action'),
      available: t('helpPage.contact4Available'),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('helpPage.title')} | mwbTechs</title>
        <meta name="description" content="Find answers to common questions and get help with mwbTechs products." />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-6">
                <HelpCircle className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">{t('helpPage.title')}</h1>
              <p className="text-xl text-gray-600">{t('helpPage.subtitle')}</p>
            </motion.div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="section">
          <div className="container-custom">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">{t('helpPage.getInTouch')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactMethods.map((method, index) => {
                const Icon = method.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all text-center"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{method.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{method.description}</p>
                    <p className="text-xs text-gray-500 mb-4">{method.available}</p>
                    <button className="btn btn-outline w-full">{method.action}</button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">{t('helpPage.faqTitle')}</h2>
            <div className="max-w-4xl mx-auto space-y-8">
              {faqs.map((category, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{category.category}</h3>
                  <div className="space-y-4">
                    {category.questions.map((item, i) => (
                      <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                        <h4 className="font-bold text-gray-900 mb-2">{item.q}</h4>
                        <p className="text-gray-600">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">{t('helpPage.stillNeedHelp')}</h2>
            <p className="text-xl text-blue-100 mb-8">{t('helpPage.stillNeedHelpDesc')}</p>
            <Link to="/contact" className="btn btn-white text-lg">{t('helpPage.contactSupport')}</Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default HelpPage;
